<template>
  <el-dialog
    v-model="visible"
    title=""
    :width="dialogWidth"
    :close-on-click-modal="false"
    append-to-body
    class="release-notes-dialog"
    @open="onOpen"
    @closed="onClosed"
  >
    <template #header>
      <div class="release-header">
        <div class="release-icon">
          <MdiIcon
            name="PartyPopper"
            :size="28"
          />
        </div>
        <div class="release-title-section">
          <h2>Nouveautés</h2>
          <span class="release-version">Version {{ currentRelease?.version }}</span>
        </div>
      </div>
    </template>

    <div class="release-content">
      <!-- Navigation si plusieurs releases : liste des versions groupées par mois -->
      <nav
        v-if="filteredReleases.length > 1"
        ref="nav"
        class="release-nav"
        aria-label="Liste des versions"
      >
        <div
          v-for="group in releaseGroups"
          :key="group.key + '-' + group.entries[0].index"
          class="release-month-group"
        >
          <div class="release-month-label">
            {{ group.label }}
          </div>
          <button
            v-for="entry in group.entries"
            :key="entry.release.version"
            :class="['release-nav-btn', { active: currentIndex === entry.index }]"
            :aria-current="currentIndex === entry.index ? 'true' : null"
            @click="currentIndex = entry.index"
          >
            <span class="release-nav-version">v{{ entry.release.version }}</span>
            <span class="release-nav-date">{{ formatShortDate(entry.release.date) }}</span>
          </button>
        </div>
      </nav>

      <!-- Contenu de la release -->
      <div
        v-if="currentRelease"
        class="release-details"
      >
        <div class="release-meta">
          <span class="release-date">
            <MdiIcon
              name="Calendar"
              :size="14"
            />
            {{ formatDate(currentRelease.date) }}
          </span>
          <h3 class="release-subtitle">
            {{ currentRelease.title }}
          </h3>
        </div>

        <!-- Une section par nature de changement : chacune peut porter des groupes avec sous-titre -->
        <div
          v-for="section in sections"
          :key="section.key"
          class="release-section"
        >
          <h4>
            <MdiIcon
              :name="section.icon"
              :size="16"
            />
            {{ section.label }}
          </h4>
          <template
            v-for="(entry, idx) in section.entries"
            :key="section.key + '-' + idx"
          >
            <!-- Groupe avec sous-titre -->
            <template v-if="entry.title">
              <h5 class="release-group-title">
                {{ entry.title }}
              </h5>
              <ul>
                <li
                  v-for="(item, j) in entry.items"
                  :key="section.key + '-' + idx + '-' + j"
                >
                  {{ item.text || item }}
                </li>
              </ul>
            </template>
            <!-- Item simple (string ou objet { text }) -->
            <ul v-else>
              <li>{{ entry.text || entry }}</li>
            </ul>
          </template>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="release-footer">
        <el-checkbox
          v-model="dontShowAgain"
          label="Ne plus afficher pour cette version"
          size="small"
        />
        <el-button
          type="primary"
          @click="handleClose"
        >
          C'est noté !
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { markVersionAsSeen, filterReleasesForScope } from '@/config/releases';

export default {
  name: 'ReleaseNotesDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    releases: {
      type: Array,
      default: () => [],
    },
    scope: {
      type: String,
      default: 'internal',
      validator: (v) => ['internal', 'extranet'].includes(v),
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      currentIndex: 0,
      dontShowAgain: true,
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    filteredReleases() {
      return filterReleasesForScope(this.releases, this.scope);
    },
    currentRelease() {
      return this.filteredReleases[this.currentIndex] || null;
    },
    sections() {
      if (!this.currentRelease) return [];
      return [
        { key: 'features', icon: 'Star', label: 'Nouvelles fonctionnalités' },
        { key: 'improvements', icon: 'TrendingUp', label: 'Améliorations' },
        { key: 'fixes', icon: 'Wrench', label: 'Corrections' },
      ]
        .map(section => ({ ...section, entries: this.currentRelease[section.key] || [] }))
        .filter(section => section.entries.length > 0);
    },
    // Versions regroupées par mois de publication, dans l'ordre de la liste d'origine
    // (la plus récente en premier). L'index d'origine est conservé pour la sélection.
    releaseGroups() {
      const groups = [];
      this.filteredReleases.forEach((release, index) => {
        const key = release.date ? String(release.date).slice(0, 7) : 'unknown';
        let group = groups[groups.length - 1];
        if (!group || group.key !== key) {
          group = { key, label: this.formatMonth(key), entries: [] };
          groups.push(group);
        }
        group.entries.push({ release, index });
      });
      return groups;
    },
    dialogWidth() {
      return this.filteredReleases.length > 1 ? '880px' : '700px';
    },
  },
  methods: {
    formatShortDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    },
    // Le libellé est construit depuis la clé du groupe (« 2026-08 ») et non depuis la date
    // de la première release : à midi et le 15, ni le fuseau ni un jour hors plage
    // (« 2026-02-29 ») ne peuvent faire basculer l'en-tête sur le mois voisin.
    formatMonth(key) {
      const date = new Date(`${key}-15T12:00:00`);
      if (Number.isNaN(date.getTime())) return 'Sans date';
      return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },
    handleClose() {
      this.visible = false;
    },
    // Réouverture : revenir à la version la plus récente et remonter la colonne, sinon la
    // liste reste positionnée là où elle avait été laissée, sans version active visible.
    onOpen() {
      this.currentIndex = 0;
      this.$nextTick(() => {
        if (this.$refs.nav) this.$refs.nav.scrollTop = 0;
      });
    },
    onClosed() {
      // La case « Ne plus afficher » est cochée par défaut : la respecter quel que soit
      // le moyen de fermeture. Ne marquer que depuis « C'est noté ! » ramenait la fenêtre
      // à chaque rechargement pour qui ferme par la croix ou par Échap.
      if (this.dontShowAgain) {
        markVersionAsSeen();
      }
    },
  },
};
</script>

<style scoped>
.release-notes-dialog :deep(.el-dialog) {
  border-radius: 16px;
  overflow: hidden;
}

.release-notes-dialog :deep(.el-dialog__header) {
  padding: 20px 24px 16px;
  margin: 0;
  background: linear-gradient(135deg, #02264a 0%, #764ba2 100%);
}

.release-notes-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
  color: white;
}

.release-notes-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.release-notes-dialog :deep(.el-dialog__footer) {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
}

.release-header {
  display: flex;
  align-items: center;
  gap: 16px;
  color: white;
}

.release-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.release-title-section h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.release-version {
  font-size: 13px;
  opacity: 0.9;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
}

.release-content {
  display: flex;
  align-items: stretch;
}

/* Colonne des versions : scroll indépendant du contenu */
.release-nav {
  flex: 0 0 190px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 12px 10px;
  background: #fafafa;
  border-right: 1px solid #f0f0f0;
}

.release-month-label {
  position: sticky;
  top: -12px;
  z-index: 1;
  padding: 6px 6px 4px;
  background: #fafafa;
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  letter-spacing: 0.3px;
  text-transform: capitalize;
}

.release-month-group + .release-month-group {
  margin-top: 6px;
}

.release-nav-btn {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  margin-bottom: 2px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #606266;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
}

.release-nav-date {
  font-size: 11px;
  color: #a8abb2;
}

.release-nav-btn:hover {
  background: white;
  border-color: #dcdfe6;
  color: #02264a;
}

.release-nav-btn.active {
  background: #02264a;
  border-color: #02264a;
  color: white;
}

.release-nav-btn.active .release-nav-date {
  color: rgba(255, 255, 255, 0.7);
}

/* Colonne de lecture */
.release-details {
  flex: 1;
  min-width: 0;
  max-height: 60vh;
  overflow-y: auto;
  padding: 20px 24px;
}

@media (max-width: 767px) {
  .release-content {
    flex-direction: column;
  }

  .release-nav {
    flex: 0 0 auto;
    max-height: 30vh;
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
  }

  .release-details {
    max-height: 50vh;
  }
}

.release-meta {
  margin-bottom: 20px;
}

.release-date {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

.release-subtitle {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.release-section {
  margin-bottom: 20px;
}

.release-section:last-child {
  margin-bottom: 0;
}

.release-section h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.release-section h4 .mdi-icon {
  color: #02264a;
}

.release-group-title {
  margin: 14px 0 6px 0;
  font-size: 13px;
  font-weight: 600;
  color: #02264a;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.release-group-title:first-of-type {
  margin-top: 4px;
}

.release-section ul {
  margin: 0 0 4px 0;
  padding-left: 20px;
}

.release-section li {
  margin-bottom: 8px;
  font-size: 14px;
  color: #606266;
  line-height: 1.5;
}

.release-section li:last-child {
  margin-bottom: 0;
}

.release-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.release-footer :deep(.el-checkbox__label) {
  font-size: 13px;
  color: #909399;
}
</style>

<style>
/* Styles globaux pour le dialog (rendu hors du composant) */
.release-notes-dialog .el-dialog {
  border-radius: 16px;
  overflow: hidden;
  max-width: calc(100vw - 32px);
}

.release-notes-dialog .el-dialog__header {
  padding: 20px 24px 16px !important;
  margin: 0 !important;
  background: linear-gradient(135deg, #02264a 0%, #6ed6ce 100%) !important;
}

.release-notes-dialog .el-dialog__headerbtn {
  top: 20px !important;
  right: 16px !important;
}

.release-notes-dialog .el-dialog__headerbtn .el-dialog__close,
.release-notes-dialog .el-dialog__headerbtn .el-icon,
.release-notes-dialog .el-dialog__headerbtn svg,
.release-notes-dialog .el-dialog__headerbtn i {
  color: white !important;
  fill: white !important;
  font-size: 20px !important;
}

.release-notes-dialog .el-dialog__headerbtn:hover .el-dialog__close,
.release-notes-dialog .el-dialog__headerbtn:hover .el-icon,
.release-notes-dialog .el-dialog__headerbtn:hover svg,
.release-notes-dialog .el-dialog__headerbtn:hover i {
  color: rgba(255, 255, 255, 0.7) !important;
  fill: rgba(255, 255, 255, 0.7) !important;
}

.release-notes-dialog .el-dialog__body {
  padding: 0 !important;
}

.release-notes-dialog .el-dialog__footer {
  padding: 16px 24px !important;
  border-top: 1px solid #f0f0f0;
}

/* Bouton principal avec la bonne couleur */
.release-notes-dialog .el-button--primary {
  background-color: #02264a !important;
  border-color: #02264a !important;
}

.release-notes-dialog .el-button--primary:hover,
.release-notes-dialog .el-button--primary:focus {
  background-color: #033566 !important;
  border-color: #033566 !important;
}

.release-notes-dialog .el-button--primary:active {
  background-color: #021d3a !important;
  border-color: #021d3a !important;
}

/* Checkbox avec la bonne couleur */
.release-notes-dialog .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: #02264a !important;
  border-color: #02264a !important;
}

.release-notes-dialog .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #02264a !important;
}

.release-notes-dialog .el-checkbox__inner:hover {
  border-color: #02264a !important;
}

.release-notes-dialog .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: #02264a !important;
}

/* ===== Dark mode styles ===== */
html.dark .release-notes-dialog .el-dialog {
  background-color: #1e1e1e;
}

html.dark .release-notes-dialog .el-dialog__footer {
  border-top-color: #3a3a3a;
}

html.dark .release-notes-dialog .release-content {
  background-color: #1e1e1e;
}

html.dark .release-notes-dialog .release-nav {
  background: #252525;
  border-right-color: #3a3a3a;
  border-bottom-color: #3a3a3a;
}

html.dark .release-notes-dialog .release-month-label {
  background: #252525;
  color: #808080;
}

html.dark .release-notes-dialog .release-nav-btn {
  background: transparent;
  border-color: transparent;
  color: #e0e0e0;
}

html.dark .release-notes-dialog .release-nav-date {
  color: #808080;
}

html.dark .release-notes-dialog .release-nav-btn:hover {
  background: #2a2a2a;
  border-color: #4a4a4a;
  color: #6ed6ce;
}

html.dark .release-notes-dialog .release-nav-btn.active {
  background: #6ed6ce;
  border-color: #6ed6ce;
  color: #02264a;
}

html.dark .release-notes-dialog .release-nav-btn.active .release-nav-date {
  color: rgba(2, 38, 74, 0.7);
}

html.dark .release-notes-dialog .release-date {
  color: #a0a0a0;
}

html.dark .release-notes-dialog .release-subtitle {
  color: #e0e0e0;
}

html.dark .release-notes-dialog .release-section h4 {
  color: #e0e0e0;
}

html.dark .release-notes-dialog .release-group-title {
  color: #6ed6ce;
}

html.dark .release-notes-dialog .release-section h4 .mdi-icon {
  color: #6ed6ce;
}

html.dark .release-notes-dialog .release-section li {
  color: #c0c0c0;
}

/* Checkbox dark mode */
html.dark .release-notes-dialog .el-checkbox__label {
  color: #a0a0a0 !important;
}

html.dark .release-notes-dialog .el-checkbox__inner {
  background-color: #2a2a2a;
  border-color: #4a4a4a;
}

html.dark .release-notes-dialog .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: #6ed6ce !important;
  border-color: #6ed6ce !important;
}

html.dark .release-notes-dialog .el-checkbox__input.is-checked + .el-checkbox__label {
  color: #6ed6ce !important;
}

html.dark .release-notes-dialog .el-checkbox__inner:hover {
  border-color: #6ed6ce !important;
}

/* Bouton en dark mode */
html.dark .release-notes-dialog .el-button--primary {
  background-color: #6ed6ce !important;
  border-color: #6ed6ce !important;
  color: #02264a !important;
}

html.dark .release-notes-dialog .el-button--primary:hover,
html.dark .release-notes-dialog .el-button--primary:focus {
  background-color: #5bc4bc !important;
  border-color: #5bc4bc !important;
}
</style>
